/**
 * QR 리더 입력 정규화
 *
 * QR 토큰은 (소문자 영문 + 숫자 + 대시 + 특수문자) 조합이지만,
 * OS/브라우저 입력기(IME)가 한글 상태이면 리더가 보낸 영문 키가 한글 자모로 들어온다.
 * 이 경우 두벌식 자판 기준으로 한글 → 영문 키를 역매핑한 뒤 소문자로 변환한다.
 */

// 두벌식 자판: 한글 자모 → 영문 키 (겹자모는 두 글자로 복원)
const JAMO_TO_KEY: Record<string, string> = {
	// 자음
	ㄱ: 'r',
	ㄲ: 'R',
	ㄳ: 'rt',
	ㄴ: 's',
	ㄵ: 'sw',
	ㄶ: 'sg',
	ㄷ: 'e',
	ㄸ: 'E',
	ㄹ: 'f',
	ㄺ: 'fr',
	ㄻ: 'fa',
	ㄼ: 'fq',
	ㄽ: 'ft',
	ㄾ: 'fx',
	ㄿ: 'fv',
	ㅀ: 'fg',
	ㅁ: 'a',
	ㅂ: 'q',
	ㅃ: 'Q',
	ㅄ: 'qt',
	ㅅ: 't',
	ㅆ: 'T',
	ㅇ: 'd',
	ㅈ: 'w',
	ㅉ: 'W',
	ㅊ: 'c',
	ㅋ: 'z',
	ㅌ: 'x',
	ㅍ: 'v',
	ㅎ: 'g',
	// 모음
	ㅏ: 'k',
	ㅐ: 'o',
	ㅑ: 'i',
	ㅒ: 'O',
	ㅓ: 'j',
	ㅔ: 'p',
	ㅕ: 'u',
	ㅖ: 'P',
	ㅗ: 'h',
	ㅘ: 'hk',
	ㅙ: 'ho',
	ㅚ: 'hl',
	ㅛ: 'y',
	ㅜ: 'n',
	ㅝ: 'nj',
	ㅞ: 'np',
	ㅟ: 'nl',
	ㅠ: 'b',
	ㅡ: 'm',
	ㅢ: 'ml',
	ㅣ: 'l'
};

// 완성형 한글(가 ~ 힣) 유니코드 조합 규칙
const HANGUL_START = 0xac00;
const HANGUL_END = 0xd7a3;
const JUNGSEONG_COUNT = 21;
const JONGSEONG_COUNT = 28;

const CHOSEONG_LIST = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const JUNGSEONG_LIST = [
	'ㅏ', 'ㅐ', 'ㅑ', 'ㅒ', 'ㅓ', 'ㅔ', 'ㅕ', 'ㅖ', 'ㅗ', 'ㅘ', 'ㅙ', 'ㅚ', 'ㅛ', 'ㅜ', 'ㅝ', 'ㅞ', 'ㅟ', 'ㅠ', 'ㅡ', 'ㅢ', 'ㅣ'
];
const JONGSEONG_LIST = [
	'', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ', 'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'
];

// 한글 한 글자 → 영문 키 (완성형은 초/중/종성으로 분해, 매핑 불가 문자는 원문 유지)
export const hangulCharToAlphabet = (char: string): string => {
	const code = char.charCodeAt(0);

	// 완성형 한글 (예: '한' → ㅎ + ㅏ + ㄴ → gks)
	if (code >= HANGUL_START && code <= HANGUL_END) {
		const index = code - HANGUL_START;
		const choseong = CHOSEONG_LIST[Math.floor(index / (JUNGSEONG_COUNT * JONGSEONG_COUNT))];
		const jungseong = JUNGSEONG_LIST[Math.floor(index / JONGSEONG_COUNT) % JUNGSEONG_COUNT];
		const jongseong = JONGSEONG_LIST[index % JONGSEONG_COUNT];
		return [choseong, jungseong, jongseong].map(jamo => JAMO_TO_KEY[jamo] ?? '').join('');
	}

	// 조합되지 않은 낱자 자모 (예: 'ㅎ')
	return JAMO_TO_KEY[char] ?? char;
};

/**
 * QR 리더가 읽은 문자열을 서버 조회용 토큰으로 정규화
 * - 한글로 입력된 문자는 두벌식 자판 기준 영문으로 변환
 * - 영문은 모두 소문자로 변환 (CapsLock/Shift 로 대문자 입력된 경우 대비)
 * - 숫자/대시/특수문자는 그대로 유지
 */
export const normalizeQrToken = (raw: string): string =>
	Array.from(raw.trim())
		.map(hangulCharToAlphabet)
		.join('')
		.toLowerCase();

/**
 * 물리 키 코드(event.code) → 문자
 *
 * 한글 IME 조합 중에는 event.key 가 'Process' / 'Unidentified' 로 들어와
 * 문자를 알 수 없으므로, 물리 키 위치로 영문/숫자/대시를 복원한다.
 * (Shift 조합 특수문자는 복원 대상이 아니므로 빈 문자열 반환)
 */
export const charFromKeyCode = (code: string): string => {
	if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase();
	if (/^Digit[0-9]$/.test(code)) return code.slice(5);
	if (code === 'Minus') return '-';
	return '';
};
